import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { PatchAppointmentStatus, GetSlot } from "@/services/fhir";
import { DeleteWhereby } from "@/services/whereby/DeleteWhereby";
import { PutPaymentNotice } from "@/services/fhir/paymentnotice/PutPaymentNotice";
import { useDeleteEvent } from "@/services/google/DeleteEvent";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetPaymentNotice } from "@/services/fhir/paymentnotice/GetPaymentNotice";
import { endpoints } from "@/configs/Settings";
import { PostWhereby } from "@/services/whereby/PostWhereby";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";

interface UseAppointmentRescheduleOptions {
  appointment: any;
  enabled: boolean;
  payment?: any;
  isPaid?: boolean;
  onSuccess?: () => void;
}

export function useAppointmentReschedule({
  appointment,
  enabled,
  payment,
  isPaid,
  onSuccess,
}: UseAppointmentRescheduleOptions) {
  const [loading, setLoading] = useState(false);
  const { accessTokenGoogle, user } = useSessionContext();
  const { deleteEvent } = useDeleteEvent();
  const [paymentNotice, setPaymentNotice] = useState<any>([]);
  const [isPaidNotice, setIsPaidNotice] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [currentSlot, setCurrentSlot] = useState<any>(null);

  const slotsByDate = useMemo(() => {
    return availableSlots.reduce((acc: any, slot: any) => {
      const dateKey = dayjs(slot.start).format("YYYY-MM-DD");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(slot);
      return acc;
    }, {});
  }, [availableSlots]);

  const availableDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  useEffect(() => {
    if (availableDates.length > 0 && !selectedDate) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);

  const auxPayment = payment?.length > 0 ? payment : paymentNotice;
  const auxIsPaid = isPaid || isPaidNotice;
  const hasPaymentNotice = (paymentNotice ?? []).length > 0;

  const fetchPayment = async () => {
    try {
      if (!appointment?.id) return;

      const patientParticipant = appointment.participant?.find((p: any) =>
        p.actor?.reference?.startsWith("Patient/")
      );
      const slotReference = appointment.slot?.[0]?.reference;

      if (patientParticipant?.actor?.reference && slotReference) {
        const filter = `status=active&_sort=-_lastUpdated&request=${slotReference}&response=${patientParticipant.actor.reference}&_count=1`;
        const response = await GetPaymentNotice({ filter });
        const entry = response?.data?.entry ?? [];
        setPaymentNotice(entry);

        const hasPaymentStatus =
          entry?.[0]?.resource?.paymentStatus?.coding?.[0]?.code === "cleared";
        setIsPaidNotice(!!hasPaymentStatus);
      }
    } catch (error) {
      console.error("Erro ao buscar pagamento:", error);
    }
  };

  const fetchSlots = async () => {
    setLoadingSlots(true);
    try {
      const slotReference = appointment.slot?.[0]?.reference;
      if (!slotReference) throw new Error("Slot reference not found");

      const slotId = slotReference.split("/")[1];
      const currentSlotResponse = await GetSlot({ id: slotId });

      const currentSlotData =
        currentSlotResponse.resourceType === "Bundle"
          ? currentSlotResponse?.entry?.[0]?.resource
          : currentSlotResponse;

      if (!currentSlotData?.schedule?.reference) {
        throw new Error("Schedule reference not found");
      }

      setCurrentSlot(currentSlotData);

      const scheduleId = currentSlotData.schedule.reference.split("/")[1];
      const startDate = dayjs().format("YYYY-MM-DD");
      const endDate = dayjs().add(30, "day").format("YYYY-MM-DD");

      const response = await GetSlot({
        schedule: scheduleId,
        status: ["free"],
        sort: "start",
        startDate,
        endDate,
      });

      const slots = response?.entry?.map((entry: any) => entry.resource) || [];
      const futureSlots = slots.filter((slot: any) =>
        dayjs(slot.start).isAfter(dayjs())
      );

      const isProposed = appointment.status === "proposed";
      if (isProposed && currentSlotData) {
        const currentSlotInList = futureSlots.find(
          (slot: any) => slot.id === currentSlotData.id
        );

        if (
          !currentSlotInList &&
          dayjs(currentSlotData.start).isAfter(dayjs())
        ) {
          futureSlots.push(currentSlotData);
          futureSlots.sort((a: any, b: any) =>
            dayjs(a.start).isBefore(dayjs(b.start)) ? -1 : 1
          );
        }
      }

      setAvailableSlots(futureSlots);
    } catch (error) {
      console.error("Erro ao buscar horários:", error);
      toast.error("Erro ao buscar horários disponíveis");
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;

    fetchPayment();
    fetchSlots();
    setAvailableSlots([]);
    setSelectedSlot("");
    setSelectedDate("");
    setCurrentSlot(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment?.id, enabled]);

  const performCancellation = async (skipPaymentCancel: boolean = false) => {
    if (!skipPaymentCancel && auxPayment?.length > 0) {
      const paymentCopy = { ...auxPayment?.[0]?.resource };
      paymentCopy.status = "cancelled";
      await PutPaymentNotice({
        paymentId: auxPayment?.[0]?.resource?.id,
        data: paymentCopy,
      });
    }

    const googleCalendarExt = appointment?.extension?.find((ext: any) =>
      ext.url.includes("googleCalendarId")
    );

    if (
      googleCalendarExt?.valueString &&
      accessTokenGoogle &&
      user?.refreshTokenGoogle
    ) {
      try {
        await deleteEvent({
          eventId: googleCalendarExt.valueString,
          accessTokenGoogle,
          refreshToken: user.refreshTokenGoogle,
        });
      } catch (err) {
        console.error("Erro ao excluir evento do Google Calendar:", err);
      }
    }

    if (appointment?.id) {
      await PatchAppointmentStatus({
        appointmentId: appointment.id,
        status: "cancelled",
      });
    }

    if (appointment?.virtualService) {
      DeleteWhereby(appointment?.virtualService?.[0]?.sessionKey as string);
    }
  };

  const handleReschedule = async () => {
    if (!selectedSlot) return;

    setLoading(true);

    const paymentToUpdate =
      auxPayment?.length > 0 ? { ...auxPayment[0].resource } : null;

    try {
      const targetSlot = availableSlots.find((s) => s.id === selectedSlot);
      if (!targetSlot) throw new Error("Slot not found");

      const isCurrentSlot = currentSlot?.id === selectedSlot;
      const isProposed = appointment.status === "proposed";

      if (isCurrentSlot && isProposed) {
        const newStatus =
          !hasPaymentNotice || auxIsPaid ? "booked" : "pending";

        await PatchAppointmentStatus({
          appointmentId: appointment.id,
          status: newStatus,
        });

        toast.success("Consulta confirmada com sucesso!");
        onSuccess?.();
        return;
      }

      try {
        await performCancellation(true);
      } catch (cancelError) {
        console.error("Error cancelling old appointment:", cancelError);
        throw new Error(
          "Falha ao cancelar o agendamento anterior. Tente novamente."
        );
      }

      const isBooked = appointment.status === "booked";
      const newStatus =
        isBooked || !hasPaymentNotice || auxIsPaid ? "booked" : "pending";

      let newAppointmentData: any = {
        resourceType: "Appointment",
        status: newStatus,
        description: appointment.description,
        serviceType: appointment.serviceType,
        appointmentType: appointment.appointmentType,
        participant: appointment.participant,
        slot: [{ reference: `Slot/${selectedSlot}` }],
        start: targetSlot.start,
        end: targetSlot.end,
        extension: appointment.extension
          ? appointment.extension.filter(
              (ext: any) => !ext.url.includes("googleCalendarId")
            )
          : undefined,
      };

      if (appointment.virtualService) {
        try {
          const auxData = {
            endDate: dayjs(targetSlot.end).format("YYYY-MM-DDTHH:mm:ss-03:00"),
            roomNamePrefix: `${endpoints?.AppName}-whereby`,
            roomNamePattern: "human-short",
          };
          const response = await PostWhereby(auxData);
          newAppointmentData.virtualService = [
            {
              addressUrl: response.roomUrl,
              sessionKey: response.meetingId || "",
            },
          ];
        } catch (err) {
          console.error("Error creating whereby room", err);
        }
      }

      const url = `${endpoints.EHRPOSTER}/Appointment`;
      const headers = {
        Authorization: await useAuth.getUserToken(),
        "Content-Type": "application/fhir+json",
        Accept: "application/fhir+json",
      };

      const response = await useAxios.post(
        url,
        JSON.stringify(newAppointmentData),
        { headers, timeout: 20000 }
      );
      const newAppointment = response.data;

      if (!newAppointment || !newAppointment.id) {
        throw new Error("Failed to create new appointment");
      }

      if (paymentToUpdate) {
        paymentToUpdate.request = { reference: `Slot/${selectedSlot}` };
        paymentToUpdate.status = "active";

        await PutPaymentNotice({
          paymentId: paymentToUpdate.id,
          data: paymentToUpdate,
        });
      }

      toast.success("Consulta reagendada com sucesso!");
      onSuccess?.();
    } catch (error: any) {
      console.error("Erro ao reagendar:", error);
      toast.error(
        error.message ||
          "Erro ao reagendar consulta. Nenhuma alteração foi feita."
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    loadingSlots,
    availableSlots,
    availableDates,
    slotsByDate,
    selectedDate,
    setSelectedDate,
    selectedSlot,
    setSelectedSlot,
    currentSlot,
    handleReschedule,
  };
}
